import type { AuthenticatedUser } from '@blueshift-ui/auth/dist/types';

import { Navigate, createBrowserRouter } from 'react-router-dom';
import ACCOUNT_ROUTES from '../../constants/account-routes';
import EXTERNAL_ROUTES from '../../constants/external-routes';
import ErrorPage from '../../pages/error';
import { Helmet } from 'react-helmet-async';
import LegacySettingsPage from '../../pages/legacy-settings';
import MembershipCancelPage from '../../pages/membership/cancel';
import MembershipUpdatePage from '../../pages/membership/update';
import SettingsContentLayout from '../../layout/settings/content';
import SettingsPageLayout from '../../layout/settings/page';
import { useMemo } from 'react';

function useCreateRouter(authenticatedUser?: AuthenticatedUser, isVt4sClient?: boolean) {
  return useMemo(() => {
    return createBrowserRouter(
      [
        {
          element: <SettingsPageLayout user={authenticatedUser} withNav={false} />,
          children: [
            isVt4sClient
              ? {}
              : {
                  path: 'membership',
                  element: <LegacySettingsPage routePath={EXTERNAL_ROUTES.membership} />,
                },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/dashboard'),
              path: '/',
            },
            !isVt4sClient
              ? {
                  errorElement: <ErrorPage variant="oops" />,
                  lazy: () => import('../../pages/activity'),
                  path: ACCOUNT_ROUTES.activity,
                }
              : {},
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/password'),
              path: ACCOUNT_ROUTES.password,
            },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/payments'),
              path: ACCOUNT_ROUTES.payment,
            },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/session-recording'),
              path: ACCOUNT_ROUTES.sessions,
            },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/your-program'),
              path: ACCOUNT_ROUTES.program,
            },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/contact-information'),
              path: ACCOUNT_ROUTES.contact,
            },
            {
              errorElement: <ErrorPage variant="oops" />,
              lazy: () => import('../../pages/signed-contracts'),
              path: ACCOUNT_ROUTES.agreements,
            },
            {
              element: <Navigate replace to={`/${ACCOUNT_ROUTES.contact}`} />,
              path: ACCOUNT_ROUTES.settings,
            },
            {
              path: 'membership/:subscriptionId',
              element: <SettingsContentLayout />,
              children: [
                {
                  path: 'update',
                  element: (
                    <>
                      <Helmet>
                        <title>Varsity Tutors | Client Account Membership Update</title>
                      </Helmet>
                      <MembershipUpdatePage />
                    </>
                  ),
                  errorElement: <ErrorPage variant="oops" />,
                },
              ],
            },
          ],
        },
        {
          element: <SettingsPageLayout user={authenticatedUser} withNav={false} />,
          children: [
            {
              path: 'membership/:subscriptionId/cancel',
              element: (
                <>
                  <Helmet>
                    <title>Varsity Tutors | Client Account Membership Cancel</title>
                  </Helmet>
                  <MembershipCancelPage />
                </>
              ),
            },
            {
              path: '*',
              element: <ErrorPage variant="not_found" />,
            },
          ],
        },
      ],
      {
        basename: '/client',
      }
    );
  }, [authenticatedUser, isVt4sClient]);
}

export default useCreateRouter;
